import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Ticket, Calendar, Plus, Settings, BarChart3, 
  Users, CheckCircle, Clock, DollarSign, Edit2, Trash2,
  ChevronDown, ChevronUp, Download, ExternalLink, CreditCard, Crown,
  Mail, MessageSquare, Send, RotateCcw, MoreVertical, QrCode
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { PremiumUpgradeCard } from '../components/PremiumUpgradeCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getAuthHeaders } from '../hooks/useAdmin';
import { BankDetailsForm } from '../components/BankDetailsForm';

import { API_URL } from '../utils/api';

export default function ClubTicketsPage() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [club, setClub] = useState(null);
  const [clubEvents, setClubEvents] = useState([]);
  const [ticketedEvents, setTicketedEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [salesReport, setSalesReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);
  
  // Dialogs
  const [showTicketTypeDialog, setShowTicketTypeDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showOrdersDialog, setShowOrdersDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [resendingOrder, setResendingOrder] = useState(null); // Track which order is being resent
  
  // Form states
  const [editingTicketType, setEditingTicketType] = useState(null);
  const [ticketTypeForm, setTicketTypeForm] = useState({
    name: '',
    description: '',
    price: '',
    capacity: '',
    max_per_order: '10',
    is_active: true
  });
  
  const [eventSettings, setEventSettings] = useState({
    ticketing_enabled: true,
    absorb_fees: false,
    platform_fee: 0.50,
    external_ticket_url: '',
    is_charity_event: false,
    charity_name: '',
    event_capacity: ''  // Shared capacity across all ticket types
  });
  
  // Offline sale state
  const [showOfflineSaleDialog, setShowOfflineSaleDialog] = useState(false);
  const [offlineSaleForm, setOfflineSaleForm] = useState({
    ticket_type_id: '',
    quantity: '1',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    payment_method: 'cash',
    notes: '',
    send_sms: false
  });
  const [offlineSaleLoading, setOfflineSaleLoading] = useState(false);

  useEffect(() => {
    if (clubId) {
      fetchClub();
      fetchClubEvents();
      fetchTicketedEvents();
      fetchBankDetails();
    }
  }, [clubId]);

  const fetchBankDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/clubs/${clubId}/bank-details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHasBankDetails(data.has_bank_details);
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    }
  };

  const fetchClub = async () => {
    try {
      console.log('Fetching club:', clubId);
      const res = await fetch(`${API_URL}/api/clubs/${clubId}`);
      console.log('Club fetch response status:', res.status);
      if (res.ok) {
        const text = await res.text();
        console.log('Club data raw:', text);
        const data = JSON.parse(text);
        console.log('Club ticketing_enabled:', data.ticketing_enabled);
        setClub(data);
      } else {
        console.error('Club fetch failed:', res.status);
      }
    } catch (error) {
      console.error('Error fetching club:', error);
    }
  };

  const fetchClubEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events?club_id=${clubId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setClubEvents(data.events || data || []);
      }
    } catch (error) {
      console.error('Error fetching club events:', error);
    }
  };

  const fetchTicketedEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/club/${clubId}/events`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTicketedEvents(data);
      }
    } catch (error) {
      console.error('Error fetching ticketed events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketTypes = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/types/event/${eventId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTicketTypes(data);
      }
    } catch (error) {
      console.error('Error fetching ticket types:', error);
    }
  };

  const fetchEventSettings = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/events/${eventId}/settings`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setEventSettings({
          ...data,
          event_capacity: data.event_capacity || ''  // Convert null to empty string for input
        });
      }
    } catch (error) {
      console.error('Error fetching event settings:', error);
    }
  };

  const fetchOrders = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/orders/event/${eventId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Export attendees for an event
  const exportEventAttendees = async (event) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/attendees/${event.event_id}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        if (!data.attendees || data.attendees.length === 0) {
          toast.info('No attendees to export yet');
          return;
        }
        
        const headers = ['Name', 'Email', 'Ticket Type', 'Quantity', 'Status', 'Checked In', 'Purchase Date', 'Booking Ref'];
        const rows = data.attendees.map(a => [
          a.name || '',
          a.email || '',
          a.ticket_type || '',
          a.quantity || 1,
          a.status || '',
          a.checked_in ? 'Yes' : 'No',
          a.purchase_date ? new Date(a.purchase_date).toLocaleDateString() : '',
          a.booking_ref || ''
        ]);
        
        const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendees-${event.slug || event.event_id}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.attendees.length} attendees`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to export attendees');
      }
    } catch (error) {
      console.error('Error exporting attendees:', error);
      toast.error('Network error');
    }
  };

  const fetchSalesReport = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/reports/event/${eventId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSalesReport(data);
      }
    } catch (error) {
      console.error('Error fetching sales report:', error);
    }
  };

  const handleEventSelect = async (event) => {
    setSelectedEvent(event);
    if (expandedEvent === event.event_id) {
      setExpandedEvent(null);
    } else {
      setExpandedEvent(event.event_id);
      await fetchTicketTypes(event.event_id);
      await fetchEventSettings(event.event_id);
    }
  };

  const handleEnableTicketing = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/events/${eventId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          ticketing_enabled: true,
          absorb_fees: eventSettings.absorb_fees,
          platform_fee: 0.50,
          is_charity_event: eventSettings.is_charity_event || false,
          charity_name: eventSettings.charity_name || null
        })
      });
      
      if (res.ok) {
        toast.success('Ticketing enabled for this event');
        setShowEnableDialog(false);
        fetchTicketedEvents();
        fetchClubEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to enable ticketing');
      }
    } catch (error) {
      toast.error('Failed to enable ticketing');
    }
  };

  const handleCreateTicketType = async () => {
    if (!selectedEvent) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tickets/types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          event_id: selectedEvent.event_id,
          name: ticketTypeForm.name,
          description: ticketTypeForm.description || null,
          price: parseFloat(ticketTypeForm.price),
          capacity: parseInt(ticketTypeForm.capacity),
          max_per_order: parseInt(ticketTypeForm.max_per_order),
          is_active: ticketTypeForm.is_active
        })
      });
      
      if (res.ok) {
        toast.success('Ticket type created');
        setShowTicketTypeDialog(false);
        resetTicketTypeForm();
        fetchTicketTypes(selectedEvent.event_id);
        fetchTicketedEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create ticket type');
      }
    } catch (error) {
      toast.error('Failed to create ticket type');
    }
  };

  const handleUpdateTicketType = async () => {
    if (!editingTicketType) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tickets/types/${editingTicketType.ticket_type_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: ticketTypeForm.name,
          description: ticketTypeForm.description || null,
          price: parseFloat(ticketTypeForm.price),
          capacity: parseInt(ticketTypeForm.capacity),
          max_per_order: parseInt(ticketTypeForm.max_per_order),
          is_active: ticketTypeForm.is_active
        })
      });
      
      if (res.ok) {
        toast.success('Ticket type updated');
        setShowTicketTypeDialog(false);
        setEditingTicketType(null);
        resetTicketTypeForm();
        fetchTicketTypes(selectedEvent.event_id);
        fetchTicketedEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update ticket type');
      }
    } catch (error) {
      toast.error('Failed to update ticket type');
    }
  };

  const handleDeleteTicketType = async (ticketTypeId) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/types/${ticketTypeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Ticket type deleted');
        fetchTicketTypes(selectedEvent.event_id);
        fetchTicketedEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete ticket type');
      }
    } catch (error) {
      toast.error('Failed to delete ticket type');
    }
  };

  const handleUpdateSettings = async () => {
    if (!selectedEvent) return;
    
    try {
      // Convert event_capacity to number or null
      const settingsToSend = {
        ...eventSettings,
        event_capacity: eventSettings.event_capacity ? parseInt(eventSettings.event_capacity, 10) : null
      };
      
      const res = await fetch(`${API_URL}/api/tickets/events/${selectedEvent.event_id}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(settingsToSend)
      });
      
      if (res.ok) {
        toast.success('Settings updated');
        setShowSettingsDialog(false);
        fetchTicketedEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleOfflineSale = async () => {
    if (!selectedEvent || !offlineSaleForm.ticket_type_id) return;
    
    setOfflineSaleLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/offline-sale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          event_id: selectedEvent.event_id,
          ticket_type_id: offlineSaleForm.ticket_type_id,
          quantity: parseInt(offlineSaleForm.quantity, 10),
          customer_name: offlineSaleForm.customer_name,
          customer_email: offlineSaleForm.customer_email,
          customer_phone: offlineSaleForm.customer_phone || null,
          payment_method: offlineSaleForm.payment_method,
          notes: offlineSaleForm.notes || null,
          send_sms: offlineSaleForm.send_sms || false
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowOfflineSaleDialog(false);
        setOfflineSaleForm({
          ticket_type_id: '',
          quantity: '1',
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          payment_method: 'cash',
          notes: '',
          send_sms: false
        });
        // Refresh data
        fetchTicketTypes(selectedEvent.event_id);
        fetchTicketedEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create offline sale');
      }
    } catch (error) {
      toast.error('Failed to create offline sale');
    } finally {
      setOfflineSaleLoading(false);
    }
  };

  const resetTicketTypeForm = () => {
    setTicketTypeForm({
      name: '',
      description: '',
      price: '',
      capacity: '',
      max_per_order: '10',
      is_active: true
    });
  };

  const openEditDialog = (ticketType) => {
    setEditingTicketType(ticketType);
    setTicketTypeForm({
      name: ticketType.name,
      description: ticketType.description || '',
      price: ticketType.price.toString(),
      capacity: ticketType.capacity.toString(),
      max_per_order: (ticketType.max_per_order || 10).toString(),
      is_active: ticketType.is_active
    });
    setShowTicketTypeDialog(true);
  };

  const openViewOrders = async (event) => {
    setSelectedEvent(event);
    await fetchOrders(event.event_id);
    setShowOrdersDialog(true);
  };

  const openViewReport = async (event) => {
    setSelectedEvent(event);
    await fetchSalesReport(event.event_id);
    setShowReportDialog(true);
  };

  const handleResendTickets = async (orderId, sendEmail = true, sendSms = false) => {
    setResendingOrder(orderId);
    try {
      const res = await fetch(`${API_URL}/api/tickets/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          order_id: orderId,
          send_email: sendEmail,
          send_sms: sendSms
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const methods = [];
        if (data.results?.email?.success) methods.push('email');
        if (data.results?.sms?.success) methods.push('SMS');
        toast.success(`Tickets resent via ${methods.join(' and ') || 'requested channels'}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to resend tickets');
      }
    } catch (error) {
      toast.error('Failed to resend tickets');
    } finally {
      setResendingOrder(null);
    }
  };

  const handleDownloadPDF = async (orderId) => {
    try {
      toast.info('Generating PDF...');
      const res = await fetch(`${API_URL}/api/tickets/orders/${orderId}/pdf`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tickets_${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF downloaded!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Get club events that can have ticketing enabled
  const availableEvents = clubEvents.filter(e => 
    !ticketedEvents.find(te => te.event_id === e.event_id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  // Check if club has ticketing feature enabled
  if (club && !club.ticketing_enabled) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Dashboard
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Ticket className="w-7 h-7 text-slate-600" />
              Event Ticketing
            </h1>
            <p className="text-slate-400 mt-1">{club?.name}</p>
          </div>
        </div>
        
        <div className="max-w-xl mx-auto px-4 py-8">
          <PremiumUpgradeCard 
            clubId={clubId} 
            clubName={club?.name} 
            currentTier={club?.subscription_tier || 'free'} 
          />
          
          <div className="mt-6 text-center">
            <p className="text-slate-500 text-sm">
              Already have Premium? <button onClick={() => window.location.reload()} className="text-violet-400 hover:underline">Refresh page</button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if club has bank details - required before selling tickets
  if (club && club.ticketing_enabled && !hasBankDetails) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Dashboard
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Ticket className="w-7 h-7 text-emerald-400" />
              Event Ticketing
            </h1>
            <p className="text-slate-400 mt-1">{club?.name}</p>
          </div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="bg-slate-900 border border-violet-500/30 rounded-xl p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Bank Details Required</h2>
                <p className="text-slate-400 max-w-lg">
                  Before you can start selling tickets, we need your bank details so we can transfer 
                  your ticket sale proceeds. This is a one-time setup.
                </p>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-6">
              <BankDetailsForm 
                clubId={clubId} 
                onComplete={() => {
                  setHasBankDetails(true);
                  toast.success('Bank details saved! You can now manage ticket sales.');
                }}
                showTitle={false}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Ticket className="w-7 h-7 text-emerald-400" />
                Event Ticketing
              </h1>
              <p className="text-slate-400 mt-1">{club?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Enable Ticketing Section */}
        {availableEvents.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-white mb-4">Enable Ticketing for an Event</h3>
            <div className="flex gap-4 flex-wrap">
              <Select onValueChange={(eventId) => {
                const event = availableEvents.find(e => e.event_id === eventId);
                if (event) {
                  setSelectedEvent(event);
                  setShowEnableDialog(true);
                }
              }}>
                <SelectTrigger className="w-full md:w-80 bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select an event to enable ticketing..." />
                </SelectTrigger>
                <SelectContent>
                  {availableEvents.map(event => {
                    let dateStr = '';
                    if (event.date) {
                      try {
                        const d = new Date(event.date);
                        dateStr = isNaN(d.getTime()) ? '' : ` - ${d.toLocaleDateString('en-GB')}`;
                      } catch {
                        dateStr = '';
                      }
                    }
                    return (
                      <SelectItem key={event.event_id} value={event.event_id}>
                        {event.title}{dateStr}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Ticketed Events */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-white">Ticketed Events</h2>
          
          {ticketedEvents.length === 0 ? (
            <div className="text-center py-16 bg-slate-900 rounded-xl border border-slate-800">
              <Ticket className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-400 mb-2">No ticketed events yet</h3>
              <p className="text-slate-400">Select an event above to enable ticket sales</p>
            </div>
          ) : (
            ticketedEvents.map(event => (
              <motion.div 
                key={event.event_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
              >
                {/* Event Header */}
                <div 
                  className="p-5 cursor-pointer hover:bg-slate-800/50 transition-colors"
                  onClick={() => handleEventSelect(event)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold text-white text-lg">{event.title}</h3>
                        <Badge variant={event.ticket_stats?.total_sold > 0 ? "default" : "secondary"}>
                          {event.ticket_stats?.total_sold || 0} / {event.ticket_stats?.total_capacity || 0} sold
                        </Badge>
                      </div>
                      <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {(event.event_date || event.date) ? (
                          new Date(event.event_date || event.date).toString() !== 'Invalid Date' 
                            ? new Date(event.event_date || event.date).toLocaleDateString('en-GB', { 
                                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                              })
                            : 'Date not set'
                        ) : 'Date not set'}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      {/* Check-In and Export buttons */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/events/${event.event_id}/checkin`, '_blank');
                          }}
                          title="Open ticket scanner"
                        >
                          <QrCode className="w-4 h-4 mr-1" />
                          Check-In
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportEventAttendees(event);
                          }}
                          title="Export attendees"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Export
                        </Button>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-400">
                          £{(event.ticket_stats?.total_revenue || 0).toFixed(2)}
                        </p>
                        <p className="text-slate-400 text-sm">Revenue</p>
                      </div>
                      {expandedEvent === event.event_id ? (
                        <ChevronUp className="w-6 h-6 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedEvent === event.event_id && (
                  <div className="border-t border-slate-800 p-5 space-y-6">
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => {
                          resetTicketTypeForm();
                          setEditingTicketType(null);
                          setShowTicketTypeDialog(true);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-500"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Ticket Type
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setOfflineSaleForm({
                            ticket_type_id: ticketTypes[0]?.ticket_type_id || '',
                            quantity: '1',
                            customer_name: '',
                            customer_email: '',
                            payment_method: 'cash',
                            notes: ''
                          });
                          setShowOfflineSaleDialog(true);
                        }}
                        disabled={ticketTypes.length === 0}
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-600/10"
                      >
                        <Ticket className="w-4 h-4 mr-2" />
                        Add Offline Sale
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          fetchEventSettings(event.event_id);
                          setShowSettingsDialog(true);
                        }}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openViewOrders(event)}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        View Orders
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openViewReport(event)}
                      >
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Sales Report
                      </Button>
                    </div>

                    {/* Ticket Types */}
                    <div>
                      <h4 className="font-medium text-slate-300 mb-3">Ticket Types</h4>
                      {ticketTypes.length === 0 ? (
                        <p className="text-slate-400 py-4">No ticket types created yet. Add one above to start selling tickets.</p>
                      ) : (
                        <div className="grid gap-3">
                          {ticketTypes.map(tt => (
                            <div 
                              key={tt.ticket_type_id}
                              className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-white text-lg">{tt.name}</span>
                                  {!tt.is_active && (
                                    <Badge variant="secondary">Inactive</Badge>
                                  )}
                                  {tt.is_sold_out && (
                                    <Badge variant="destructive">Sold Out</Badge>
                                  )}
                                </div>
                                {tt.description && (
                                  <p className="text-slate-400 text-sm mt-1">{tt.description}</p>
                                )}
                                <p className="text-slate-400 mt-2">
                                  <span className="text-emerald-400 font-semibold">£{tt.price.toFixed(2)}</span>
                                  <span className="mx-2">·</span>
                                  <span>{tt.sold_count || 0} / {tt.capacity} sold</span>
                                  <span className="mx-2">·</span>
                                  <span>{tt.capacity - (tt.sold_count || 0)} available</span>
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(tt)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                {(tt.sold_count || 0) === 0 && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Ticket Type</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete "{tt.name}"? This cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleDeleteTicketType(tt.ticket_type_id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Enable Ticketing Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enable Ticketing</DialogTitle>
            <DialogDescription>
              Enable ticket sales for "{selectedEvent?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Absorb Fees</Label>
                <p className="text-slate-400 text-sm">
                  {eventSettings.absorb_fees 
                    ? "You pay the platform & processing fees" 
                    : "Buyer pays the platform fee (£0.50/ticket)"}
                </p>
              </div>
              <Switch
                checked={eventSettings.absorb_fees}
                onCheckedChange={(checked) => setEventSettings({...eventSettings, absorb_fees: checked})}
              />
            </div>
            
            {/* Charity Event Option */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="flex items-center gap-2">
                  <span>🎗️</span> Charity Event
                </Label>
                <p className="text-slate-400 text-sm">
                  {eventSettings.is_charity_event 
                    ? "Buyers can choose to cover fees" 
                    : "Standard ticketing"}
                </p>
              </div>
              <Switch
                checked={eventSettings.is_charity_event}
                onCheckedChange={(checked) => setEventSettings({
                  ...eventSettings, 
                  is_charity_event: checked,
                  absorb_fees: checked ? true : eventSettings.absorb_fees
                })}
              />
            </div>
            
            {eventSettings.is_charity_event && (
              <div className="bg-emerald-900/30 border border-emerald-800 rounded-lg p-4">
                <Label htmlFor="charity_name_enable" className="text-emerald-300">Charity Name (optional)</Label>
                <Input
                  id="charity_name_enable"
                  value={eventSettings.charity_name || ''}
                  onChange={(e) => setEventSettings({...eventSettings, charity_name: e.target.value})}
                  placeholder="e.g., Cancer Research UK"
                  className="mt-2 bg-slate-800 border-slate-600"
                />
                <p className="text-emerald-400/70 text-xs mt-2">
                  Buyers will see an option to cover the booking fee so 100% goes to charity
                </p>
              </div>
            )}
            
            <div className="bg-slate-800 rounded-lg p-4 text-sm">
              <p className="text-slate-300 font-medium mb-2">Fee Structure</p>
              <div className="space-y-1 text-slate-400">
                <p>• Platform fee: £0.50 per ticket</p>
                <p>• Square processing: ~2.5% + 20p</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnableDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleEnableTicketing(selectedEvent?.event_id)}>
              Enable Ticketing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Type Dialog */}
      <Dialog open={showTicketTypeDialog} onOpenChange={setShowTicketTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTicketType ? 'Edit Ticket Type' : 'Create Ticket Type'}
            </DialogTitle>
            <DialogDescription>
              {editingTicketType 
                ? 'Update the ticket type details' 
                : 'Add a new ticket type for this event'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="e.g., General Admission, VIP, Early Bird"
                value={ticketTypeForm.name}
                onChange={(e) => setTicketTypeForm({...ticketTypeForm, name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What's included with this ticket?"
                value={ticketTypeForm.description}
                onChange={(e) => setTicketTypeForm({...ticketTypeForm, description: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="10.00"
                  value={ticketTypeForm.price}
                  onChange={(e) => setTicketTypeForm({...ticketTypeForm, price: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Capacity *</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="100"
                  value={ticketTypeForm.capacity}
                  onChange={(e) => setTicketTypeForm({...ticketTypeForm, capacity: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Max per Order</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={ticketTypeForm.max_per_order}
                onChange={(e) => setTicketTypeForm({...ticketTypeForm, max_per_order: e.target.value})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={ticketTypeForm.is_active}
                onCheckedChange={(checked) => setTicketTypeForm({...ticketTypeForm, is_active: checked})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketTypeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={editingTicketType ? handleUpdateTicketType : handleCreateTicketType}
              disabled={!ticketTypeForm.name || !ticketTypeForm.price || !ticketTypeForm.capacity}
            >
              {editingTicketType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ticket Settings</DialogTitle>
            <DialogDescription>
              Configure ticketing options for this event
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Ticketing Enabled</Label>
                <p className="text-slate-400 text-sm">Allow ticket sales for this event</p>
              </div>
              <Switch
                checked={eventSettings.ticketing_enabled}
                onCheckedChange={(checked) => setEventSettings({...eventSettings, ticketing_enabled: checked})}
              />
            </div>
            
            {/* External Ticket URL Option */}
            <div className="border-t border-slate-700 pt-4">
              <Label>External Ticket Provider</Label>
              <p className="text-slate-400 text-sm mb-2">
                If you use another platform for ticket sales (e.g. Eventbrite, Ticketmaster), 
                enter the URL here. Leave empty to use our built-in ticketing.
              </p>
              <Input
                placeholder="https://www.eventbrite.co.uk/e/your-event"
                value={eventSettings.external_ticket_url || ''}
                onChange={(e) => setEventSettings({...eventSettings, external_ticket_url: e.target.value})}
                className="mt-2 placeholder:text-slate-500 placeholder:opacity-50"
              />
              {eventSettings.external_ticket_url && (
                <div className="mt-2 flex items-center gap-2 text-sm text-amber-400">
                  <ExternalLink className="w-4 h-4" />
                  <span>Buyers will be redirected to this external site</span>
                </div>
              )}
            </div>
            
            {!eventSettings.external_ticket_url && (
              <div className="border-t border-slate-700 pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Absorb Fees</Label>
                    <p className="text-slate-400 text-sm">
                      {eventSettings.absorb_fees 
                        ? "You pay the platform & processing fees" 
                        : "Buyer pays the platform fee (£0.50/ticket)"}
                    </p>
                  </div>
                  <Switch
                    checked={eventSettings.absorb_fees}
                    onCheckedChange={(checked) => setEventSettings({...eventSettings, absorb_fees: checked})}
                  />
                </div>
                
                {/* Charity Event Option */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <span>🎗️</span> Charity Event
                    </Label>
                    <p className="text-slate-400 text-sm">
                      {eventSettings.is_charity_event 
                        ? "Buyers can choose to cover fees (100% goes to charity)" 
                        : "Standard ticketing fees apply"}
                    </p>
                  </div>
                  <Switch
                    checked={eventSettings.is_charity_event}
                    onCheckedChange={(checked) => setEventSettings({
                      ...eventSettings, 
                      is_charity_event: checked,
                      absorb_fees: checked ? true : eventSettings.absorb_fees  // Auto-absorb fees for charity
                    })}
                  />
                </div>
                
                {eventSettings.is_charity_event && (
                  <div className="bg-emerald-900/30 border border-emerald-800 rounded-lg p-4">
                    <Label htmlFor="charity_name" className="text-emerald-300">Charity Name (optional)</Label>
                    <Input
                      id="charity_name"
                      value={eventSettings.charity_name || ''}
                      onChange={(e) => setEventSettings({...eventSettings, charity_name: e.target.value})}
                      placeholder="e.g., Cancer Research UK"
                      className="mt-2 bg-slate-800 border-slate-600"
                    />
                    <p className="text-emerald-400/70 text-xs mt-2">
                      Buyers will see: "I'll cover the booking fee so 100% goes to {eventSettings.charity_name || 'charity'}"
                    </p>
                  </div>
                )}
                
                {/* Shared Event Capacity */}
                <div className="border-t border-slate-700 pt-4">
                  <Label>Shared Event Capacity</Label>
                  <p className="text-slate-400 text-sm mb-2">
                    Total attendee limit across all ticket types (leave empty for individual limits)
                  </p>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g., 125"
                    value={eventSettings.event_capacity}
                    onChange={(e) => setEventSettings({...eventSettings, event_capacity: e.target.value})}
                    className="bg-slate-800 border-slate-600"
                  />
                  {eventSettings.event_capacity && (
                    <p className="text-xs text-teal-400 mt-1">
                      All ticket types will share this capacity. When {eventSettings.event_capacity} tickets are sold, all types show "Sold Out".
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSettings}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orders Dialog */}
      <Dialog open={showOrdersDialog} onOpenChange={setShowOrdersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Orders</DialogTitle>
            <DialogDescription>
              {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {orders.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No orders yet</p>
            ) : (
              orders.map(order => (
                <div 
                  key={order.order_id}
                  className="p-4 bg-slate-800 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-white">{order.customer_name}</p>
                      <p className="text-slate-400 text-sm">{order.customer_email}</p>
                      {order.customer_phone && (
                        <p className="text-slate-500 text-xs">{order.customer_phone}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-400 text-lg">£{order.total_amount?.toFixed(2)}</p>
                      <Badge variant={order.status === 'completed' ? 'success' : 'secondary'}>
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-slate-400">
                      <span>{order.ticket_count} ticket(s)</span>
                      <span className="mx-2">•</span>
                      <span>{new Date(order.created_at).toLocaleString()}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          disabled={resendingOrder === order.order_id}
                          className="text-slate-400 hover:text-white hover:bg-slate-700"
                        >
                          {resendingOrder === order.order_id ? (
                            <RotateCcw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <MoreVertical className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="z-[200]">
                        <DropdownMenuItem onClick={() => handleResendTickets(order.order_id, true, false)}>
                          <Mail className="w-4 h-4 mr-2" />
                          Resend via Email
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleResendTickets(order.order_id, false, true)}
                          disabled={!order.customer_phone}
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Resend via SMS
                          {!order.customer_phone && <span className="text-xs text-slate-500 ml-1">(no phone)</span>}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleResendTickets(order.order_id, true, true)}
                          disabled={!order.customer_phone}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Resend Both
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDownloadPDF(order.order_id)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sales Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sales Report</DialogTitle>
            <DialogDescription>
              {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>
          
          {salesReport && (
            <div className="space-y-6 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-400">
                    £{salesReport.summary?.total_revenue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-slate-400 text-sm">Total Revenue</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-violet-400">
                    {salesReport.summary?.total_sold || 0}
                  </p>
                  <p className="text-slate-400 text-sm">Tickets Sold</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">
                    {salesReport.summary?.total_orders || 0}
                  </p>
                  <p className="text-slate-400 text-sm">Orders</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-400">
                    {salesReport.summary?.sell_through_rate || 0}%
                  </p>
                  <p className="text-slate-400 text-sm">Sell-through</p>
                </div>
              </div>

              {/* Ticket Type Breakdown */}
              <div>
                <h4 className="font-medium text-white mb-3">By Ticket Type</h4>
                <div className="space-y-2">
                  {salesReport.ticket_types?.map(tt => (
                    <div 
                      key={tt.ticket_type_id}
                      className="flex items-center justify-between p-4 bg-slate-800 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-white">{tt.name}</p>
                        <p className="text-slate-400 text-sm">£{tt.price?.toFixed(2)} each</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">{tt.sold_count || 0} / {tt.capacity}</p>
                        <p className="text-emerald-400 text-sm">
                          £{((tt.sold_count || 0) * tt.price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Check-in Stats */}
              <div className="bg-slate-800 rounded-lg p-4">
                <h4 className="font-medium text-white mb-3">Check-in Status</h4>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-slate-300">
                      {salesReport.summary?.checked_in || 0} checked in
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-400">
                      {(salesReport.summary?.total_sold || 0) - (salesReport.summary?.checked_in || 0)} pending
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Close
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offline Sale Dialog */}
      <Dialog open={showOfflineSaleDialog} onOpenChange={setShowOfflineSaleDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Offline Sale</DialogTitle>
            <DialogDescription>
              Create tickets for cash or offline payments. Tickets will be emailed to the customer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ticket Type</Label>
              <Select
                value={offlineSaleForm.ticket_type_id}
                onValueChange={(value) => setOfflineSaleForm({...offlineSaleForm, ticket_type_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select ticket type" />
                </SelectTrigger>
                <SelectContent>
                  {ticketTypes.map(tt => (
                    <SelectItem key={tt.ticket_type_id} value={tt.ticket_type_id}>
                      {tt.name} - £{tt.price.toFixed(2)} ({tt.capacity - (tt.sold_count || 0)} available)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={offlineSaleForm.quantity}
                onChange={(e) => setOfflineSaleForm({...offlineSaleForm, quantity: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                value={offlineSaleForm.customer_name}
                onChange={(e) => setOfflineSaleForm({...offlineSaleForm, customer_name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={offlineSaleForm.customer_email}
                onChange={(e) => setOfflineSaleForm({...offlineSaleForm, customer_email: e.target.value})}
                placeholder="john@example.com"
              />
              <p className="text-xs text-slate-500">Tickets will be emailed to this address</p>
            </div>
            
            <div className="space-y-2">
              <Label>Phone Number (for SMS)</Label>
              <Input
                type="tel"
                value={offlineSaleForm.customer_phone || ''}
                onChange={(e) => setOfflineSaleForm({...offlineSaleForm, customer_phone: e.target.value})}
                placeholder="07123 456789"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send_sms"
                checked={offlineSaleForm.send_sms || false}
                onChange={(e) => setOfflineSaleForm({...offlineSaleForm, send_sms: e.target.checked})}
                className="rounded border-slate-600"
              />
              <Label htmlFor="send_sms" className="text-sm cursor-pointer">
                Also send tickets via SMS
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={offlineSaleForm.payment_method}
                onValueChange={(value) => setOfflineSaleForm({...offlineSaleForm, payment_method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={offlineSaleForm.notes}
                onChange={(e) => setOfflineSaleForm({...offlineSaleForm, notes: e.target.value})}
                placeholder="Any internal notes..."
              />
            </div>
            
            {offlineSaleForm.ticket_type_id && offlineSaleForm.quantity && (
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm text-slate-300">
                  <span className="font-medium">Total:</span>{' '}
                  £{(
                    (ticketTypes.find(t => t.ticket_type_id === offlineSaleForm.ticket_type_id)?.price || 0) * 
                    parseInt(offlineSaleForm.quantity || 0, 10)
                  ).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mt-1">No platform fees for offline sales</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfflineSaleDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleOfflineSale}
              disabled={offlineSaleLoading || !offlineSaleForm.ticket_type_id || !offlineSaleForm.customer_name || !offlineSaleForm.customer_email}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {offlineSaleLoading ? 'Creating...' : 'Create Tickets'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
